<script setup lang="ts">
import { ref, toRef, computed } from 'vue'

const props = defineProps<{
  productDetail: any
}>()
const { t } = useLocale()

const excludedProperties: string[] = [
  '_id',
  'category',
  'name',
  'description',
  'image',
  'allergens',
  'productComposition',
]
const filteredProperties = ref<{ [key: string]: any }>({})
const productDetailRef = toRef(props, 'productDetail')

for (const key in productDetailRef.value) {
  if (!excludedProperties.includes(key)) {
    const value = productDetailRef.value[key]
    // If the value is null to assign empty value
    value ? (filteredProperties.value[key] = value) : (filteredProperties.value[key] = '')
  }
}

const isNotEmptyNutritionalInfo = computed(() => {
  return !!Object.keys(filteredPropertiesWithPercentage.value).length
})

const cards = computed(() => {
  const keys = ['calories', 'lipids', 'carbohydrates', 'proteins', 'sodium']
  const cards = []

  for (const key of keys) {
    const item = filteredPropertiesWithPercentage.value.find(item => item?.basePropertyName === key)
    if (item) {
      const value = getValue(item)
      const percentage = item?.percentagePropertyValue ? item?.percentagePropertyValue + '%' : ''
      const showPercentage = ['lipids', 'carbohydrates'].includes(key)
      cards.push({
        value,
        itemKey: key,
        percentage,
        showPercentage,
      })
    }
  }

  return cards
})

const getColumnValue = (propertyName: string) => {
  // 1.- validate if it contains property value
  // 2.- percentage could be '' or has value so add it to the array for the nutritional table
  const basePropertyValue = filteredProperties.value[propertyName]
  const percentagePropertyValue = filteredProperties.value[`${propertyName}Percentage`]
  if (basePropertyValue !== '') {
    const result = { basePropertyName: propertyName, basePropertyValue, percentagePropertyValue }
    return result
  }
  return null
}

const filteredPropertiesWithPercentage = computed(() => {
  const result = []

  for (const propertyName in filteredProperties.value) {
    if (!propertyName.endsWith('Percentage') && !propertyName.endsWith('Suffix')) {
      const item = getColumnValue(propertyName)
      if (item) {
        result.push(item)
      }
    }
  }
  return result
})

const getRowClass = (index: number) => {
  if (index % 2 === 0) {
    return 'bg-white'
  } else {
    return 'bg-mcd-secondaryIvory'
  }
}

const getValue = (item: any) => {
  const itemName = item?.basePropertyName
  const unit = getUnit(itemName)
  const value = `${item.basePropertyValue}${unit}`
  return value
}

const getUnit = (itemKey?: string) => {
  if (!itemKey) return ''
  if (itemKey === 'sodium') return 'mg'
  else if (itemKey === 'calories') return 'Kcal'
  else return 'g'
}
</script>
<template>
  <section class="mt-6">
    <template v-if="isNotEmptyNutritionalInfo">
      <div class="my-1 mt-6 flex flex-row flex-wrap justify-center gap-2 py-2">
        <NutritionalCard
          v-for="card in cards"
          :key="card.itemKey"
          :value="card.value"
          :item-key="card.itemKey"
          :percentage="card.percentage"
          :show-percentage="card.showPercentage"
        />
      </div>
      <table class="my-1 w-full table-fixed">
        <thead class="bg-[#EAEAEA] text-xs">
          <tr>
            <th class="w-6/12 p-4 text-start">{{ t('productDetailStatic.nutritional.type') }}</th>
            <th class="p-4 pl-0 text-start">
              {{ t('productDetailStatic.nutritional.byProduct') }}
            </th>
            <th class="p-4 pl-0 text-start">{{ t('productDetailStatic.nutritional.idr') }}%</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in filteredPropertiesWithPercentage"
            :key="item?.basePropertyName"
            class="text-xs"
            :class="getRowClass(index)"
          >
            <td class="p-4">
              {{
                item?.basePropertyName
                  ? t(`productDetailStatic.nutritional.items.${item.basePropertyName}`)
                  : '-'
              }}
            </td>
            <td class="p-4 pl-0">
              {{ getValue(item) }}
            </td>
            <td class="p-4 pl-0">
              {{ item?.percentagePropertyValue ? item?.percentagePropertyValue + '%' : '' }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <template v-else>
      <p class="px-2">{{ t('productDetailStatic.nutritional.empty') }}</p>
    </template>
  </section>
</template>
